import React from "react";
import { DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar } from "./ui/calendar";
import { format, addHours } from "date-fns";
import { es } from "date-fns/locale";
import { Event, EventType } from "../types/event";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";
import { CalendarIcon } from "lucide-react";
import { Database } from "@/lib/database.types";

type DatabaseEventType = Database["public"]["Enums"]["event_type"];

const eventTypes: EventType[] = [
  "Reunión",
  "Documento",
  "Vencimiento",
  "Consulta",
  "Caso",
  "Tarea",
  "Plazo",
];

const formSchema = z
  .object({
    title: z.string().min(1, "El título es requerido"),
    type: z.enum([
      "Reunión",
      "Documento",
      "Vencimiento",
      "Consulta",
      "Caso",
      "Tarea",
      "Plazo",
    ]),
    date: z.date(),
    duration: z.string().default("01:00"),
    description: z.string().min(1, "La descripción es requerida"),
    location: z.string().optional(),
    // Case-specific fields
    caseNumber: z.string().optional(),
    clientName: z.string().optional(),
    clientEmail: z.string().optional(),
    clientPhone: z.string().optional(),
    caseStatus: z.enum(["active", "inactive", "closed", "archived"]).optional(),
    // Task-specific fields
    taskPriority: z.enum(["low", "medium", "high", "urgent"]).optional(),
    taskStatus: z
      .enum(["pending", "in_progress", "completed", "cancelled"])
      .optional(),
    // Deadline-specific fields
    reminderDays: z.number().optional(),
    deadlineStatus: z
      .enum(["pending", "completed", "overdue", "cancelled"])
      .optional(),
  })
  .required();

interface NewEventDialogProps {
  onOpenChange: (open: boolean) => void;
  onSubmit: (event: Event) => void;
  userId: string;
  initialData?: Event;
}

const mapEventTypeToDatabase = (type: EventType): DatabaseEventType => {
  switch (type) {
    case "Reunión":
      return "meeting";
    case "Documento":
      return "document_review";
    case "Vencimiento":
      return "deadline";
    case "Consulta":
      return "consultation";
    case "Caso":
      return "meeting"; // Map to meeting for now
    case "Tarea":
      return "deadline"; // Map to deadline for tasks
    case "Plazo":
      return "deadline"; // Map to deadline for deadlines
    default:
      return "meeting";
  }
};

export function NewEventDialog({
  onOpenChange,
  onSubmit,
  userId,
  initialData,
}: NewEventDialogProps) {
  const { user } = useAuth();
  const isEditing = !!initialData;



  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData
      ? {
          title: initialData.title,
          type: initialData.type,
          date: initialData.start_time
            ? new Date(initialData.start_time)
            : new Date(),
          duration:
            initialData.start_time && initialData.end_time
              ? (() => {
                  const start = new Date(initialData.start_time);
                  const end = new Date(initialData.end_time);
                  const diffMs = end.getTime() - start.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const hours = Math.floor(diffMins / 60)
                    .toString()
                    .padStart(2, "0");
                  const mins = (diffMins % 60).toString().padStart(2, "0");
                  return `${hours}:${mins}`;
                })()
              : "01:00",
          description: initialData.description,
          location: initialData.location || "",
          // Case management fields from originalData
          caseNumber: initialData.originalData?.caseNumber || "",
          clientName:
            initialData.originalData?.clientName ||
            initialData.clientName ||
            "",
          clientEmail: initialData.originalData?.clientEmail || "",
          clientPhone: initialData.originalData?.clientPhone || "",
          caseStatus: initialData.originalData?.caseStatus || "active",
          taskPriority: initialData.originalData?.taskPriority || "medium",
          taskStatus: initialData.originalData?.taskStatus || "pending",
          reminderDays: initialData.originalData?.reminderDays || 3,
          deadlineStatus: initialData.originalData?.deadlineStatus || "pending",
        }
      : {
          title: "",
          type: "Reunión",
          date: new Date(),
          duration: "01:00",
          description: "",
          location: "",
          caseNumber: "",
          clientName: "",
          clientEmail: "",
          clientPhone: "",
          caseStatus: "active",
          taskPriority: "medium",
          taskStatus: "pending",
          reminderDays: 3,
          deadlineStatus: "pending",
        },
  });

  React.useEffect(() => {
    if (initialData) {
      form.reset({
        title: initialData.title,
        type: initialData.type,
        date: initialData.start_time
          ? new Date(initialData.start_time)
          : new Date(),
        duration:
          initialData.start_time && initialData.end_time
            ? (() => {
                const start = new Date(initialData.start_time);
                const end = new Date(initialData.end_time);
                const diffMs = end.getTime() - start.getTime();
                const diffMins = Math.floor(diffMs / 60000);
                const hours = Math.floor(diffMins / 60)
                  .toString()
                  .padStart(2, "0");
                const mins = (diffMins % 60).toString().padStart(2, "0");
                return `${hours}:${mins}`;
              })()
            : "01:00",
        description: initialData.description || "",
        location: initialData.location || "",
        // Case management fields from originalData
        caseNumber: initialData.originalData?.caseNumber || "",
        clientName:
          initialData.originalData?.clientName ||
          initialData.clientName ||
          "",
        clientEmail: initialData.originalData?.clientEmail || "",
        clientPhone: initialData.originalData?.clientPhone || "",
        caseStatus: initialData.originalData?.caseStatus || "active",
        taskPriority: initialData.originalData?.taskPriority || "medium",
        taskStatus: initialData.originalData?.taskStatus || "pending",
        reminderDays: initialData.originalData?.reminderDays || 3,
        deadlineStatus: initialData.originalData?.deadlineStatus || "pending",
      });
    } else {
      form.reset({
        title: "",
        type: "Reunión",
        date: new Date(),
        duration: "01:00",
        description: "",
        location: "",
        caseNumber: "",
        clientName: "",
        clientEmail: "",
        clientPhone: "",
        caseStatus: "active",
        taskPriority: "medium",
        taskStatus: "pending",
        reminderDays: 3,
        deadlineStatus: "pending",
      });
    }
  }, [initialData, form]);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) {
      toast({
        title: "Error",
        description: "Debes estar logueado para crear eventos",
        variant: "destructive",
      });
      return;
    }

    try {
      const startTime = values.date;
      const [hours, minutes] = values.duration.split(":").map(Number);
      const endTime = new Date(startTime);
      endTime.setHours(endTime.getHours() + hours);
      endTime.setMinutes(endTime.getMinutes() + minutes);

      let result;
      const isEditing = !!initialData;

      // Insert or update into the appropriate table based on event type
      switch (values.type) {
        case "Caso":
          if (isEditing) {
            const actualId =
              initialData.originalData?.id ||
              initialData.id.replace("case-", "");

            const { data: existing } = await supabase
              .from("procesos")
              .select("metadata")
              .eq("id", actualId)
              .maybeSingle();

            const mergedMetadata = {
              ...(existing?.metadata || {}),
              client_name: values.clientName || '',
              client_email: values.clientEmail || '',
              client_phone: values.clientPhone || '',
              description: values.description || ''
            };

            result = await supabase
              .from("procesos")
              .update({
                numero_proceso: values.caseNumber || `CASE-${Date.now()}`,
                asunto: values.title,
                watch_status: values.caseStatus || "active",
                is_watched: values.caseStatus !== "closed" && values.caseStatus !== "archived",
                metadata: mergedMetadata,
              })
              .eq("id", actualId)
              .eq("user_id", user.id)
              .select()
              .single();
          } else {
            const metadata = {
              client_name: values.clientName || '',
              client_email: values.clientEmail || '',
              client_phone: values.clientPhone || '',
              description: values.description || ''
            };
            result = await supabase
              .from("procesos")
              .insert({
                user_id: user.id,
                numero_proceso: values.caseNumber || `CASE-${Date.now()}`,
                asunto: values.title,
                clase: values.title,
                watch_status: values.caseStatus || "active",
                is_watched: values.caseStatus !== "closed" && values.caseStatus !== "archived",
                metadata: metadata,
              })
              .select()
              .single();
          }
          break;

        case "Tarea":
          if (isEditing) {
            const actualId =
              initialData.originalData?.id ||
              initialData.id.replace("task-", "");
            result = await supabase
              .from("legal_tasks")
              .update({
                title: values.title,
                description: values.description,
                priority: values.taskPriority || "medium",
                status: values.taskStatus || "pending",
                due_date: startTime.toISOString(),
              })
              .eq("id", actualId)
              .eq("user_id", user.id)
              .select()
              .single();
          } else {
            result = await supabase
              .from("legal_tasks")
              .insert({
                user_id: user.id,
                case_id: null, // Could be linked to a case if needed
                title: values.title,
                description: values.description,
                priority: values.taskPriority || "medium",
                status: values.taskStatus || "pending",
                due_date: startTime.toISOString(),
              })
              .select()
              .single();
          }
          break;

        case "Plazo":
          if (isEditing) {
            const actualId =
              initialData.originalData?.id ||
              initialData.id.replace("deadline-", "");
            result = await supabase
              .from("legal_deadlines")
              .update({
                title: values.title,
                description: values.description,
                due_date: startTime.toISOString(),
                reminder_days: values.reminderDays || 3,
                status: values.deadlineStatus || "pending",
              })
              .eq("id", actualId)
              .eq("user_id", user.id)
              .select()
              .single();
          } else {
            result = await supabase
              .from("legal_deadlines")
              .insert({
                user_id: user.id,
                case_id: null, // Could be linked to a case if needed
                title: values.title,
                description: values.description,
                due_date: startTime.toISOString(),
                reminder_days: values.reminderDays || 3,
                status: values.deadlineStatus || "pending",
              })
              .select()
              .single();
          }
          break;

        default:
          // For regular calendar events (Reunión, Documento, Vencimiento, Consulta)
          if (isEditing) {
            result = await supabase
              .from("calendar_events")
              .update({
                title: values.title,
                description: values.description,
                event_type: mapEventTypeToDatabase(values.type),
                original_event_type: values.type,
                start_time: startTime.toISOString(),
                end_time: endTime.toISOString(),
                location: values.location || null,
              })
              .eq("id", initialData.id)
              .eq("user_id", user.id)
              .select()
              .single();
          } else {
            result = await supabase
              .from("calendar_events")
              .insert({
                user_id: user.id,
                title: values.title,
                description: values.description,
                event_type: mapEventTypeToDatabase(values.type),
                original_event_type: values.type,
                start_time: startTime.toISOString(),
                end_time: endTime.toISOString(),
                location: values.location || null,
              })
              .select()
              .single();
          }
          break;
      }

      if (result.error) {
        console.error("Error creating event:", result.error);
        toast({
          title: "Error",
          description: "Error al crear el evento",
          variant: "destructive",
        });
        return;
      }

      // For case management events, create a virtual Event object for the calendar
      if (["Caso", "Tarea", "Plazo"].includes(values.type)) {
        const virtualEvent: Event = {
          id: result.data.id,
          title: values.title,
          type: values.type,
          event_type: mapEventTypeToDatabase(values.type),
          original_event_type: values.type,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          description: values.description,
          status: "pending",
        };
        onSubmit(virtualEvent);
      } else {
        // For regular calendar events, use the standard Event object
        onSubmit({
          id: result.data.id,
          title: values.title,
          type: values.type,
          event_type: mapEventTypeToDatabase(values.type),
          original_event_type: values.type,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          description: values.description,
          location: values.location || null,
          status: "pending",
        });
      }

      toast({
        title: "Éxito",
        description: isEditing
          ? "Evento actualizado exitosamente"
          : "Evento creado exitosamente",
      });

      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating event:", error);
      toast({
        title: "Error",
        description: "Error inesperado al crear el evento",
        variant: "destructive",
      });
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Nuevo Evento</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título</FormLabel>
                <FormControl>
                  <Input placeholder="Título del evento" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {eventTypes.map((type, index) => (
                      <SelectItem key={`${type}-${index}`} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Dynamic fields based on event type */}
          {form.watch("type") === "Caso" && (
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="caseNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de Caso</FormLabel>
                    <FormControl>
                      <Input placeholder="Número del caso" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="clientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre del Cliente</FormLabel>
                      <FormControl>
                        <Input placeholder="Nombre del cliente" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="clientEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email del Cliente</FormLabel>
                      <FormControl>
                        <Input placeholder="Email del cliente" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="clientPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono del Cliente</FormLabel>
                    <FormControl>
                      <Input placeholder="Teléfono del cliente" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="caseStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado del Caso</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="inactive">Inactivo</SelectItem>
                        <SelectItem value="closed">Cerrado</SelectItem>
                        <SelectItem value="archived">Archivado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {form.watch("type") === "Tarea" && (
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="taskPriority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prioridad</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una prioridad" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Baja</SelectItem>
                        <SelectItem value="medium">Media</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="urgent">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taskStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado de la Tarea</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="in_progress">En Progreso</SelectItem>
                        <SelectItem value="completed">Completada</SelectItem>
                        <SelectItem value="cancelled">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {form.watch("type") === "Plazo" && (
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="reminderDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recordatorio (días antes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="3"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 3)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="deadlineStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado del Plazo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="completed">Completado</SelectItem>
                        <SelectItem value="overdue">Vencido</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Fecha y hora</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                          type="button">
                          {field.value ? (
                            format(field.value, "PPP 'a las' HH:mm", {
                              locale: es,
                            })
                          ) : (
                            <span>Selecciona una fecha</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        locale={es}
                        initialFocus
                      />
                      <div className="p-3 border-t">
                        <Input
                          type="time"
                          onChange={(e) => {
                            const [hours, minutes] = e.target.value.split(":");
                            const newDate = new Date(field.value);
                            newDate.setHours(
                              parseInt(hours),
                              parseInt(minutes)
                            );
                            field.onChange(newDate);
                          }}
                          defaultValue={format(field.value, "HH:mm")}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duración</FormLabel>
                  <FormControl>
                    <Input type="time" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descripción</FormLabel>
                <FormControl>
                  <Textarea placeholder="Descripción del evento" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ubicación (opcional)</FormLabel>
                <FormControl>
                  <Input placeholder="Ubicación del evento" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {isEditing ? "Guardar Cambios" : "Crear Evento"}
            </Button>
          </div>
        </form>
      </Form>
    </DialogContent>
  );
}
